import type { AiSuggestion } from "@shared/components/menus/ai-suggestion"
import {
  DrilldownMenu,
  MenuHeader,
  MenuRoot,
  MenuTitle,
  Row,
  Rows,
} from "@shared/components/menus/DrilldownMenu"
import { cx } from "@shared/components/untitledui/util/cx"
import { getPatternUrl } from "cc-frontend/helpers/get-cover-pattern-style"
import { useLayoutEffect, useRef, useState, type CSSProperties } from "react"
import { Button as AriaButton } from "react-aria-components"

export type { AiSuggestion }

interface Props {
  suggestions: Array<AiSuggestion>
  select: (suggestion: AiSuggestion) => void
  /** Reported so the panel can drop its focus trap while the overflow menu is open. */
  onMenuOpenChange: (isOpen: boolean) => void
}

/** Gap between the pills, matching the `gap-1.5` below. */
const GAP_PX = 6

/**
 * Every draw of a suggestion's colour, in one ladder, set on each face that owns a `--card-color`.
 * They are relative to each other by design — the wash has to stay pale enough for the ink, and
 * the pattern has to sit between the two — so moving one alone is what makes a card read wrong.
 * A tone resolves against the `--card-color` on its own element, which is why this goes on the
 * pill and the card rather than once on an ancestor.
 */
const CARD_TONES: CSSProperties = {
  "--card-wash": "color-mix(in oklab, var(--card-color) 16%, white)",
  "--card-pattern": "color-mix(in oklab, var(--card-color) 20%, transparent)",
  "--card-subtitle": "color-mix(in oklab, var(--card-color) 42%, black)",
  "--card-ink": "color-mix(in oklab, var(--card-color) 62%, black)",
} as CSSProperties

/** The suggestion's own colour, plus the ladder drawn from it. */
function toneStyle(suggestion: AiSuggestion, extra?: CSSProperties): CSSProperties {
  return { "--card-color": `var(${suggestion.colorVar})`, ...CARD_TONES, ...extra } as CSSProperties
}

/** Keeps the line weight off the artwork's own transform, so it lands on the pixel grid. */
const CRISP_STROKE = "[&_*]:[vector-effect:non-scaling-stroke]"

const PILL_ICON_CLASS = cx("size-3.5 shrink-0", CRISP_STROKE, "[&_*]:stroke-[1.5]")

/** Heavier than the pill's: at 32px a pill's weight reads as a hairline. */
const CARD_ICON_CLASS = cx("relative size-8 shrink-0", CRISP_STROKE, "[&_*]:stroke-[3]")

const PILL_CLASS =
  "flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full bg-black/3 py-1.5 pr-[13px] pl-2.5 text-[13px] leading-none font-normal text-black/60 outline-hidden transition-[transform,background-color] duration-200 hover:-translate-y-px hover:bg-black/5"

/**
 * The pattern layer. The tiles are black shapes on transparent, so DRAWING one lays grey over
 * the wash however faint it is. This paints a deeper draw of the card's own colour and uses the
 * tile as a MASK instead, which tints the hue rather than darkening it. A second mask layer,
 * intersected with the first, fades the whole thing out to the right so no word sits on texture.
 */
const CARD_PATTERN_CLASS = cx(
  // rounded-[14px], not the card's own 16px: this sits inside the 2px rim.
  "pointer-events-none absolute inset-0 rounded-[14px] bg-[var(--card-pattern)]",
  "[mask-image:var(--pattern-url),linear-gradient(to_right,black,transparent)]",
  "[mask-repeat:repeat,no-repeat] [mask-composite:intersect]",
)

/**
 * The one-line row of suggestion pills under the input, with the rest behind a "…" menu.
 *
 * The pills are measured in a hidden mirror row: the visible row can only show what fits, so it
 * cannot be its own ruler once anything has dropped out of it.
 */
export default function LessonAiSuggestions({ suggestions, select, onMenuOpenChange }: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const mirrorRef = useRef<HTMLDivElement | null>(null)
  const labelRef = useRef<HTMLSpanElement | null>(null)
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const [visibleCount, setVisibleCount] = useState(suggestions.length)

  useLayoutEffect(() => {
    const container = containerRef.current
    const mirror = mirrorRef.current
    const label = labelRef.current
    const trigger = triggerRef.current
    if (!container || !mirror || !label || !trigger) return

    const measure = () => {
      // The label and the overflow trigger share the row, so the pills only get what is left of
      // them. Both are measured rather than assumed: a hardcoded width for the trigger silently
      // stops matching the moment anyone resizes it, and the row overflows or drops a pill early.
      const takenByOthers = [label, trigger].reduce(
        (taken, element) => taken + element.getBoundingClientRect().width + GAP_PX,
        0,
      )
      const available = container.clientWidth - takenByOthers
      const widths = Array.from(mirror.children).map((pill) => pill.getBoundingClientRect().width)

      let used = 0
      let count = 0
      for (const width of widths) {
        used += (count === 0 ? 0 : GAP_PX) + width
        if (used > available) break
        count++
      }
      setVisibleCount(count)
    }

    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(container)
    return () => observer.disconnect()
  }, [suggestions])

  return (
    <div ref={containerRef} className="relative flex w-full items-center gap-1.5">
      {/* The ruler: every pill at its natural width, out of flow and out of the a11y tree. */}
      <div
        ref={mirrorRef}
        aria-hidden="true"
        className="pointer-events-none invisible absolute flex gap-1.5"
      >
        {suggestions.map((suggestion) => (
          <span key={suggestion.id} className={PILL_CLASS}>
            <suggestion.icon className={PILL_ICON_CLASS} />
            {suggestion.title}
          </span>
        ))}
      </div>

      <span
        ref={labelRef}
        data-test="ai suggestions label"
        className="shrink-0 text-[13px] leading-none font-semibold text-black/70"
      >
        Try These:
      </span>

      {suggestions.slice(0, visibleCount).map((suggestion) => (
        <AriaButton
          key={suggestion.id}
          data-test="suggestion pill"
          style={toneStyle(suggestion)}
          className={PILL_CLASS}
          onPress={() => select(suggestion)}
        >
          <suggestion.icon className={cx(PILL_ICON_CLASS, "text-[var(--card-ink)]")} />
          {suggestion.title}
        </AriaButton>
      ))}

      <DrilldownMenu
        ariaLabel="AI Suggestions menu"
        // The panel fills the width it sits in, so there is no room beside the trigger for
        // the card-stack menus' sideways placement. React Aria still flips it above when the
        // panel is low enough in the viewport that the menu would not fit below.
        placement="bottom end"
        trigger={
          <AriaButton
            ref={triggerRef}
            aria-label="More AI suggestions"
            data-test="ai suggestions trigger"
            className="flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-[20px] bg-[#f3f4f6] text-[#6b7280] outline-hidden transition duration-200 hover:-translate-y-px hover:bg-[#e5e7eb]"
          >
            <svg className="size-3 fill-current" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M0 12a3.25 3.25 0 1 0 6.5 0A3.25 3.25 0 1 0 0 12m8.75 0a3.25 3.25 0 1 0 6.5 0 3.25 3.25 0 1 0-6.5 0m8.75 0a3.25 3.25 0 1 0 6.5 0 3.25 3.25 0 1 0-6.5 0" />
            </svg>
          </AriaButton>
        }
        header={
          <MenuHeader className="border-b border-border-secondary">
            <MenuTitle>AI Suggestions</MenuTitle>
          </MenuHeader>
        }
        onOpenChange={onMenuOpenChange}
      >
        {({ close }) => (
          <MenuRoot>
            {/* The rows ARE the cards. The inset and the gaps between them belong to the list,
                so each row's own padding is the card's — nothing wraps a row to hold a look. */}
            <Rows className="gap-2 px-5 pb-3">
              {suggestions.map((suggestion) => (
                <Row
                  key={suggestion.id}
                  typeaheadText={suggestion.title}
                  style={toneStyle(suggestion, {
                    "--pattern-url": getPatternUrl(suggestion.pattern),
                  } as CSSProperties)}
                  className={cx(
                    // A wash of the course colour, not the colour itself: eleven cards at full
                    // strength is a paint chart, and the icon has nothing left to stand off.
                    "relative gap-3.5 rounded-2xl bg-[var(--card-wash)] px-4 py-2.5",
                    // A white rim and a small shadow, so each card reads as a thing you press.
                    "border-2 border-white shadow-sm transition-[transform,box-shadow] duration-200",
                    "data-hovered:-translate-y-px data-hovered:shadow-md",
                    // The keyboard gets the pointer's lift too, and the row's ring is pushed
                    // outside the white rim — inset by default, it would hide under it.
                    "data-focus-visible:-translate-y-px data-focus-visible:shadow-md",
                    "data-focus-visible:outline-offset-1",
                  )}
                  onAction={() => {
                    close()
                    select(suggestion)
                  }}
                >
                  <span
                    aria-hidden="true"
                    data-test="suggestion pattern"
                    className={CARD_PATTERN_CLASS}
                  />
                  {/* Monochrome, like everything else on the card: one hue, drawn at four
                      depths — the wash, the pattern, the icon, the words. */}
                  <suggestion.icon className={cx(CARD_ICON_CLASS, "text-[var(--card-ink)]")} />
                  {/* The two lines are one block of type, set tight: nothing between them
                      but the leading, so the subtitle reads as the title's own second line. */}
                  <span className="relative flex min-w-0 flex-col">
                    <span
                      data-test="suggestion title"
                      // #000, not `text-black`: the app's theme redefines that token as the
                      // off-black #22292f, and this line is meant to be the real thing.
                      className="text-lg leading-none font-bold text-[#000]"
                    >
                      {suggestion.title}
                    </span>
                    <span
                      data-test="suggestion subtitle"
                      className="text-sm leading-none text-[var(--card-subtitle)]"
                    >
                      {suggestion.subtitle}
                    </span>
                  </span>
                </Row>
              ))}
            </Rows>
          </MenuRoot>
        )}
      </DrilldownMenu>
    </div>
  )
}
